/**
 * Login API routing by company email domain (same UI for everyone).
 * Only Tumma (@tumma.co) is product-owner staff; all other business domains are tenants.
 */

export const STAFF_EMAIL_DOMAINS = ["tumma.co"];

/** Common consumer domains — not allowed for Cloud Office / Connect login. */
export const BLOCKED_PERSONAL_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.in",
  "yahoo.in",
  "ymail.com",
  "outlook.com",
  "outlook.in",
  "hotmail.com",
  "hotmail.co.in",
  "live.com",
  "live.in",
  "msn.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "protonmail.com",
  "proton.me",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "gmx.com",
  "rediffmail.com",
];

export function emailDomain(email) {
  const raw = String(email || "").trim().toLowerCase();
  const at = raw.lastIndexOf("@");
  if (at < 0 || at === raw.length - 1) return "";
  return raw.slice(at + 1);
}

/** Tumma staff logins: exactly @tumma.co (subdomains are not staff). */
export function isStaffEmail(email) {
  const domain = emailDomain(email);
  if (!domain) return false;
  return STAFF_EMAIL_DOMAINS.includes(domain);
}

export const PORTAL_SHARED = "Shared";
export const PORTAL_OWN_DOMAIN = "Own Domain";

export const PORTAL_LABELS = {
  [PORTAL_SHARED]: "Digix Leads CRM",
  [PORTAL_OWN_DOMAIN]: "Customer Domain",
};

export function portalTypeLabel(portalType) {
  return PORTAL_LABELS[portalType] || PORTAL_LABELS[PORTAL_SHARED];
}

/** Bare portal domain: "https://www.acme.com/x" and "office.acme.com" -> "acme.com". */
export function normalizePortalDomain(value) {
  let host = String(value || "").trim().toLowerCase();
  host = host.replace(/^[a-z]+:\/\//, "").split(/[/?#]/)[0].split(":")[0].replace(/\.$/, "");
  for (const prefix of ["www.", "office."]) {
    if (host.startsWith(prefix)) host = host.slice(prefix.length);
  }
  return host;
}

const DOMAIN_RE = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

export function isValidPortalDomain(domain) {
  return DOMAIN_RE.test(String(domain || ""));
}

/**
 * Client-side mirror of the API login-email rule for a tenant's portal type.
 * Returns an error message or "".
 */
export function tenantLoginEmailError(email, portalType, portalDomain) {
  const value = String(email || "").trim();
  if (!value) return "Login email is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Enter a valid email.";
  if (portalType === PORTAL_OWN_DOMAIN && portalDomain) {
    return emailDomain(value) === portalDomain.toLowerCase() ? "" : `Login email must end with @${portalDomain}.`;
  }
  if (isStaffEmail(value)) return "@tumma.co emails are reserved for Tumma staff.";
  return "";
}

export function isBlockedPersonalEmail(email) {
  const domain = emailDomain(email);
  if (!domain) return false;
  return BLOCKED_PERSONAL_DOMAINS.includes(domain);
}

export const COMPANY_EMAIL_PERSONAL_DOMAIN_MESSAGE =
  "Company email must use your company domain (Gmail, Outlook, Yahoo and other personal providers are not allowed).";

export function loginAudience(email) {
  if (isStaffEmail(email)) return "staff";
  return "tenant";
}
